/**
 * PetGlucoseDetailPanel - Comprehensive per-pet glucose detail (Parts 2-7)
 *
 * Extracted from the old in-place Dashboard detail so it can live on the pet
 * detail page (PetShow). Given a petId it manages its own device selection
 * (via the shared selectedDeviceId store, scoped to the pet's devices),
 * computes the full data date-range for the chosen device, and renders:
 *
 * - Part 2: Device Summary + TIR (table + vertical bar)
 * - Part 3: Trend Chart (range slider, linked to Daily TIR day selection)
 * - Part 4: Daily TIR (clickable days drive the trend range)
 * - Part 5: AGP Analysis + Vet Notes
 * - Part 6: Daily Trend + Daily Activity
 * - Part 7: Low Glucose Events
 * - Export: CSV + PDF (pet-aggregated for the active device/date range)
 */

import SettingsIcon from '@mui/icons-material/Settings'
import { Box, Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { endOfDay, parseISO, startOfDay } from 'date-fns'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useGetList, useStore, useTranslate } from 'react-admin'
import {
  getPetIdFromVirtualDevice,
  getVirtualDeviceId,
  isVirtualDevice,
} from '../../common/virtualDevice'
import { ErrorBoundary } from '../../components/ErrorBoundary'
import { GlucoseCSVExport } from '../../components/GlucoseCSVExport'
import { GlucosePDFExport } from '../../components/GlucosePDFExport'
import { TIRVerticalBarChart } from '../../components/TIRVerticalBarChart'
import EnhancedDeviceDatagrid from '../../devices/EnhancedDeviceDatagrid'
import { usePetTargetRange } from '../../hooks/usePetTargetRange'
import { DailyTIRBarChart } from '../../metrics/DailyTIRBarChart'
import { GlucoseTrendChart, type GlucoseTrendChartHandle } from '../../metrics/GlucoseTrendChart'
import { SummaryReportSection } from '../../metrics/SummaryReportSection'
import type { Device } from '../../types'
import { AGPChart } from './AGPChart'
import { DailyTrendActivitySection } from './DailyTrendActivitySection'
import { LowGlucoseEventsPanel } from './LowGlucoseEventsPanel'
import { resolveWindowStartMs, stableFallbackStartMs, stableWindowEndMs } from './petDetailWindow'
import { TIRSummaryTable } from './TIRSummaryTable'
import { TreatmentPlanPanel } from './TreatmentPlanPanel'
import { VetNotesPanel } from './VetNotesPanel'

export interface PetGlucoseDetailPanelProps {
  petId: number | string
}

export const PetGlucoseDetailPanel: React.FC<PetGlucoseDetailPanelProps> = ({ petId }) => {
  const translate = useTranslate()
  const [selectedDeviceId, setSelectedDeviceId] = useStore<any>('selectedDeviceId')
  const [glucoseUnit] = useStore('glucoseUnit', 'mmol/L')

  // Opens the trend chart's target-range dialog from the heading button — the
  // in-chart gear is easy to miss, so we surface a labeled control too. The
  // button is disabled until the chart reports it has loaded + has data (its
  // dialog only mounts then), so a click is never silently inert.
  const trendChartRef = useRef<GlucoseTrendChartHandle>(null)
  const [chartReady, setChartReady] = useState(false)

  // Effective glucose target range for this pet: the vet's per-pet override
  // (written by the trend chart's dialog, canonical mmol/L) layered over the
  // species default, sanitized. Resolving it through the shared hook keeps this
  // panel, the trend chart and the PDF export on ONE definition — the PDF used
  // to read species-only and printed a different TIR than this screen.
  // RaStore is reactive, so saving the dialog re-renders TIR / AGP / daily-trend.
  const { targetMin, targetMax, highMax } = usePetTargetRange(petId)

  // Fallback window (last 14 days) used until the device's metrics define the
  // real data span. Computed once so its identity is stable within a mount —
  // and bucketed to 5-minute boundaries so it is ALSO stable ACROSS remounts:
  // a remounting panel then reuses the react-query cache instead of re-firing
  // its full query set with never-cacheable fresh-"now" keys (the 2026-07-10
  // request storm — see stableWindowEndMs).
  const defaultDateRange = useMemo(
    () => ({
      start: new Date(stableFallbackStartMs(Date.now())),
      end: new Date(stableWindowEndMs(Date.now())),
    }),
    [],
  )
  const [selectedTirDateKeys, setSelectedTirDateKeys] = useState<string[]>([])

  // Fetch this pet's devices (a pet rarely has more than ~100 devices)
  const { data: petDevices = [] } = useGetList<Device>('devices', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'id', order: 'ASC' },
    filter: { pet: petId },
  })

  // Does the app-global selection actually belong to THIS pet? `selectedDeviceId`
  // is a single localStorage-persisted store key shared across ALL patients, so
  // when a vet opens a pet it can still hold the PREVIOUSLY-viewed patient's
  // device id (a real device or a virtual "All Devices" for another pet).
  // PROD bug 2026-06-26 (pet 285032/Lexie): that stale id was passed straight to
  // the trend chart, which fetched and rendered ANOTHER patient's glucose data
  // (May-window readings from devices on unrelated pets) while the auto-correct
  // below raced to replace it — the chart flickered between "All Devices (2)"
  // and a stale single-sensor "No data available" view. Gating every child on
  // this flag means a non-belonging id renders NOTHING (no cross-patient leak,
  // no flicker) until the effect converges the store on this pet's default.
  const selectionBelongsToPet = useMemo(() => {
    const virtualForThisPet =
      isVirtualDevice(selectedDeviceId) &&
      String(getPetIdFromVirtualDevice(selectedDeviceId)) === String(petId)
    return virtualForThisPet || petDevices.some((d) => String(d.id) === String(selectedDeviceId))
  }, [selectedDeviceId, petId, petDevices])

  // Belonging to this pet is NOT enough: the persisted selection can be one of
  // THIS pet's OLD sensors. PROD case 2026-07-09 (pet 284014/Mantou Yeh, clinic
  // 982): the vet last viewed the pet in May on sensor 291365; the pet got a
  // NEW sensor on Jul 6, so on the next visit the restored May-era id passed
  // the belongs-to-pet gate and every chart queried a device whose data ended
  // May 20 — "No data available" everywhere while the pet was streaming live.
  // So a CONCRETE (non-virtual) device selection restored from a previous visit
  // is treated as stale on a multi-device pet until this mount converges it
  // (once per pet, below) on the virtual "All Devices" default, which always
  // includes the newest sensor. Selections made mid-session (device grid click)
  // happen after convergence and are honored — vets can still deliberately
  // review an old sensor.
  //
  // The visit key is (pet, single|multi), not the pet alone (TLA+
  // DeviceSelection D1). A ONE-sensor pet converges to that sensor's concrete
  // id (R32), and nobody chose it — there was nothing to choose. When a second
  // sensor is activated and the device-list refetch lands (a cached list shows
  // at once and refetches in the background), a pet-only key had already
  // marked the visit converged, so the auto-picked old sensor was honored as
  // if clicked and every chart stayed on it while the new one streamed. Going
  // single → multi now re-arms the rule above. It deliberately does NOT re-arm
  // on multi → more: a click among two or more sensors is a real choice and
  // must survive a third sensor arriving (R33; re-arming on every length change
  // was model-checked and overrides it).
  const convergenceKey = `${petId}:${petDevices.length > 1 ? 'multi' : 'single'}`
  const [convergedKey, setConvergedKey] = useState<string | null>(null)
  const selectionIsPersistedStale =
    selectionBelongsToPet &&
    !isVirtualDevice(selectedDeviceId) &&
    petDevices.length > 1 &&
    convergedKey !== convergenceKey

  // The id actually handed to the chart/summary/TIR/export children. Undefined
  // whenever the store holds a stale cross-pet selection (never fetch another
  // patient's readings) or a not-yet-converged persisted same-pet selection
  // (never render an old sensor's empty window as if it were the pet's data).
  const effectiveDeviceId =
    selectionBelongsToPet && !selectionIsPersistedStale ? selectedDeviceId : undefined

  // Converge the shared store on THIS pet's default: the pet-level virtual
  // "All Devices" for a multi-device pet, else the single device. Runs when the
  // selection doesn't belong to this pet (cross-pet staleness, watched
  // continuously) and once per pet visit for a persisted same-pet device id
  // (same-pet staleness, see above). This panel is the SOLE auto-writer of the
  // selection (EnhancedDeviceDatagrid intentionally no longer auto-selects — a
  // second writer keyed off a different device list used to ping-pong with
  // this one).
  useEffect(() => {
    if (petDevices.length === 0) return
    if (!selectionBelongsToPet) {
      setSelectedDeviceId(petDevices.length > 1 ? getVirtualDeviceId(petId) : petDevices[0]?.id)
    } else if (selectionIsPersistedStale) {
      setSelectedDeviceId(getVirtualDeviceId(petId))
    }
    if (convergedKey !== convergenceKey) {
      setConvergedKey(convergenceKey)
    }
  }, [
    petId,
    petDevices.length,
    selectionBelongsToPet,
    selectionIsPersistedStale,
    convergedKey,
    convergenceKey,
    setSelectedDeviceId,
  ])

  // Clear linked day selection whenever the device changes
  useEffect(() => {
    setSelectedTirDateKeys([])
  }, [selectedDeviceId])

  // Date range spanning the selected device's — or, for the virtual "All
  // Devices" selection, all the pet's devices' — active period: from sensor
  // activation (device.start_time) through now.
  //
  // Derived PURELY from the reliable petDevices list (useGetList), NOT from
  // latest/earliest-metric queries. The previous metric-window approach raced
  // the metrics filter (its memo intermittently went empty during the
  // null→petId device switch, so the queries hit the GLOBAL metrics table and
  // the match guard then rejected them) which left the range stuck on the
  // default 14-day window — so the virtual-device exports/summary read empty.
  // End is "now" (stable from mount); summary/TIR/exports compute over the
  // actual readings that fall inside the window.
  // Window start as a PRIMITIVE (epoch ms) — see resolveWindowStartMs. Keeping
  // this a number means dateRange's identity below only changes when the actual
  // start changes, not every time petDevices' array ref churns on refetch (the
  // previous object-returning memo flipped identity on every refetch, which
  // cascaded re-renders). A start_time-less device falls back to the pet-wide
  // earliest instead of the 14-day default, so the window can't oscillate.
  const startMs = useMemo(
    () => resolveWindowStartMs(effectiveDeviceId, petDevices),
    [effectiveDeviceId, petDevices],
  )
  const endMs = defaultDateRange.end.getTime()
  const dateRange = useMemo(
    () => (startMs != null ? { start: new Date(startMs), end: new Date(endMs) } : defaultDateRange),
    [startMs, endMs, defaultDateRange],
  )

  const linkedTrendRange = useMemo(() => {
    if (selectedTirDateKeys.length === 0) return dateRange
    const selectedDates = selectedTirDateKeys.map((dateKey) => parseISO(dateKey))
    const minTs = Math.min(...selectedDates.map((d) => d.getTime()))
    const maxTs = Math.max(...selectedDates.map((d) => d.getTime()))
    return { start: startOfDay(new Date(minTs)), end: endOfDay(new Date(maxTs)) }
  }, [selectedTirDateKeys, dateRange])

  const handleDailyTirDayClick = (payload: { date: Date; dateKey: string; shiftKey: boolean }) => {
    const { dateKey, shiftKey } = payload
    setSelectedTirDateKeys((prev) => {
      const isSelected = prev.includes(dateKey)
      if (shiftKey) {
        return isSelected ? prev.filter((key) => key !== dateKey) : [...prev, dateKey].sort()
      }
      return isSelected ? prev.filter((key) => key !== dateKey) : [dateKey]
    })
  }

  const hasLinkedTirSelection = selectedTirDateKeys.length > 0

  return (
    <Box>
      {/* Device picker + export buttons */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              gap: 2,
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {translate('pos.dashboard.devices_section')}
            </Typography>
            {effectiveDeviceId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GlucoseCSVExport
                  petId={petId}
                  deviceId={effectiveDeviceId}
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                  size="medium"
                />
                <GlucosePDFExport
                  petId={petId}
                  deviceId={effectiveDeviceId}
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                  size="medium"
                  variant="button"
                />
              </Box>
            )}
          </Box>
          <EnhancedDeviceDatagrid rowClick="select" selectedPetId={petId} />
        </CardContent>
      </Card>

      {!effectiveDeviceId ? null : (
        <>
          {/* Chronic-uncontrolled banner — surfaces sustained out-of-range
              state without firing a push alert. Hidden when pet is in target.
              See ChronicUncontrolledBanner for the rule and rationale.
              Temporarily hidden per product request (2026-06-26). */}
          {/* <ChronicUncontrolledBanner
            deviceId={effectiveDeviceId}
            petId={petId}
            startDate={dateRange.start}
            endDate={dateRange.end}
            targetMin={targetMin}
            targetMax={targetMax}
            highMax={highMax}
          /> */}

          {/* ===== Part 2: Device Summary + TIR ===== */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }} fontWeight="bold">
                {translate('pos.glucose.summary_report')}
              </Typography>

              <SummaryReportSection
                petId={petId}
                deviceId={effectiveDeviceId}
                startDate={dateRange.start}
                endDate={dateRange.end}
                targetMin={targetMin}
                targetMax={targetMax}
                highMax={highMax}
              />

              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    {translate('pos.glucose.tir_statistics')}
                  </Typography>
                  <TIRSummaryTable
                    deviceId={effectiveDeviceId}
                    petId={petId}
                    startDate={dateRange.start}
                    endDate={dateRange.end}
                    targetMin={targetMin}
                    targetMax={targetMax}
                    highMax={highMax}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box sx={{ height: '100%', py: 1 }}>
                    <TIRVerticalBarChart
                      deviceId={effectiveDeviceId}
                      petId={petId}
                      startDate={dateRange.start}
                      endDate={dateRange.end}
                      height={300}
                      barWidth={60}
                      targetMin={targetMin}
                      targetMax={targetMax}
                      highMax={highMax}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* ===== Part 3: Trend Chart ===== */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {translate('pos.dashboard.glucose_trend')}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  onClick={() => trendChartRef.current?.openSettings()}
                  disabled={!chartReady}
                >
                  {translate('pos.glucose.configure_target_range')}
                </Button>
              </Box>
              <ErrorBoundary
                componentName={translate('pos.error_boundary.names.glucose_trend')}
                compact
              >
                {/* Keyed by patient (eng-brain P70-1, L-007): PetShow reuses this
                    panel across patients, and the chart's picked date / custom
                    range would otherwise window the next patient's chart. */}
                <GlucoseTrendChart
                  key={String(petId)}
                  ref={trendChartRef}
                  petId={petId}
                  deviceId={effectiveDeviceId}
                  enableRangeSlider={true}
                  defaultView="all"
                  startDate={hasLinkedTirSelection ? linkedTrendRange.start : undefined}
                  endDate={hasLinkedTirSelection ? linkedTrendRange.end : undefined}
                  selectedDateKeys={selectedTirDateKeys}
                  targetMin={targetMin}
                  targetMax={targetMax}
                  highMax={highMax}
                  onReadyChange={setChartReady}
                />
              </ErrorBoundary>
            </CardContent>
          </Card>

          {/* ===== Part 4: Daily TIR ===== */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {translate('pos.dashboard.daily_tir')}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!hasLinkedTirSelection}
                  onClick={() => setSelectedTirDateKeys([])}
                >
                  {translate('pos.glucose.reset_selection', { _: 'Reset selection' })}
                </Button>
              </Box>
              <ErrorBoundary
                componentName={translate('pos.error_boundary.names.daily_tir')}
                compact
              >
                <DailyTIRBarChart
                  petId={petId}
                  deviceId={effectiveDeviceId}
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                  glucoseUnit={glucoseUnit}
                  selectedDateKeys={selectedTirDateKeys}
                  onDayClick={handleDailyTirDayClick}
                  targetMin={targetMin}
                  targetMax={targetMax}
                  highMax={highMax}
                />
              </ErrorBoundary>
            </CardContent>
          </Card>

          {/* ===== Part 5: AGP Analysis + Treatment Plan + Vet Notes ===== */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }} fontWeight="bold">
                {translate('pos.agp.title')}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <ErrorBoundary componentName={translate('pos.error_boundary.names.agp')} compact>
                    <AGPChart
                      deviceId={effectiveDeviceId}
                      petId={petId}
                      startDate={dateRange.start}
                      endDate={dateRange.end}
                      height={350}
                      targetMin={targetMin}
                      targetMax={targetMax}
                    />
                  </ErrorBoundary>
                </Grid>
                <Grid item xs={12} md={5}>
                  {/* Two visually distinct sections: the structured plan sent
                      to the owner app, and the hospital-only free-text notes
                      (VP #14 — was a single Vet Notes box). */}
                  <ErrorBoundary
                    componentName={translate('pos.error_boundary.names.treatment_plan')}
                    compact
                  >
                    <TreatmentPlanPanel petId={petId} />
                  </ErrorBoundary>
                  <Divider sx={{ my: 2 }} />
                  <VetNotesPanel petId={petId} maxHeight={350} showAddForm={true} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* ===== Part 6: Daily Trend + Daily Activity ===== */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }} fontWeight="bold">
                {translate('pos.dashboard.daily_trends_and_activities', {
                  _: 'Daily Glucose Trends & Activities',
                })}
              </Typography>
              <ErrorBoundary
                componentName={translate('pos.error_boundary.names.daily_trends')}
                compact
              >
                {/* Keyed by patient (P70-2): each day's mini-chart is keyed by
                    DATE, so a zoom on pet A's "today" survived onto pet B's. */}
                <DailyTrendActivitySection
                  key={String(petId)}
                  deviceId={effectiveDeviceId}
                  petId={petId}
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                  chartHeight={150}
                  targetMin={targetMin}
                  targetMax={targetMax}
                  highMax={highMax}
                  linkedHighlightRange={hasLinkedTirSelection ? linkedTrendRange : null}
                />
              </ErrorBoundary>
            </CardContent>
          </Card>

          {/* ===== Part 7: Low Glucose Events ===== */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <ErrorBoundary
                componentName={translate('pos.error_boundary.names.low_events')}
                compact
              >
                <LowGlucoseEventsPanel
                  deviceId={effectiveDeviceId}
                  petId={petId}
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                  maxHeight={400}
                  targetMin={targetMin}
                />
              </ErrorBoundary>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  )
}

export default PetGlucoseDetailPanel
